import Button from "@/components/button/Button"
import { useAuth } from "@/components/context/AuthProvider"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import OrgItem from "@/components/modal-dark/modal-dark-platform-content/choose-portal/OrgItem"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { useOrganizationsGeneralInfoQuery } from "@/graphql"
import { PortalMismatchContent } from "@/helpers/orgSession"
import { websiteRoutes } from "@/router/routes"
import { Col, Row, Skeleton } from "antd"
import { FC, useState } from "react"
import { useNavigate } from "react-router-dom"
import "../choose-portal/ChoosePortal.less"

const { Title, Paragraph } = WebsiteTypography

const capitalize = (slug: string) => slug.charAt(0).toUpperCase() + slug.slice(1)

/**
 * Shown when the visitor is signed in to one organization's portal and tries to open, sign in to
 * or register on another one. Offers their own dashboard, or an explicit sign-out before continuing —
 * signing in elsewhere would otherwise end the current session silently.
 */
const PortalMismatch: FC = () => {
  const navigate = useNavigate()
  const { website, dashboard } = websiteRoutes
  const { user, logout } = useAuth()
  const { modalDarkContent, setIsModalDarkOpen } = useModalDarkContext()
  const { userOrgSlug, targetOrgSlug, portal, nextStep } = (modalDarkContent ?? {}) as PortalMismatchContent
  const [isSigningOut, setIsSigningOut] = useState(false)

  const { data, loading } = useOrganizationsGeneralInfoQuery({
    variables: { filter: { slug: { in: [userOrgSlug, targetOrgSlug] } } },
    skip: !userOrgSlug || !targetOrgSlug,
  })

  const organizations = (data?.organizations?.data ?? []) as OrganizationEntity[]
  const userOrg = organizations.find(it => it?.attributes?.slug === userOrgSlug)
  const userOrgName = userOrg?.attributes?.name || capitalize(userOrgSlug ?? "")
  const targetOrgName =
    organizations.find(it => it?.attributes?.slug === targetOrgSlug)?.attributes?.name ||
    capitalize(targetOrgSlug ?? "")

  const onGoToOwnDashboard = () => {
    setIsModalDarkOpen(false)
    navigate(`/${userOrgSlug}${dashboard.overview}`)
  }

  const onSignOutAndContinue = async () => {
    setIsSigningOut(true)
    await logout()
    setIsSigningOut(false)
    setIsModalDarkOpen(false)
    const mode = nextStep === "register" ? "client" : "login"
    navigate(`/${targetOrgSlug}${website.auth}/${portal}/${mode}`)
  }

  const intentText =
    nextStep === "dashboard"
      ? `The ${targetOrgName} dashboard is only available to ${targetOrgName} accounts.`
      : `To continue, you need a ${targetOrgName} account.`

  const continueLabel =
    nextStep === "register" ? `SIGN OUT & REGISTER WITH ${targetOrgName}` : `SIGN OUT & SIGN IN TO ${targetOrgName}`

  return (
    <Row justify={"center"} className={"choose-portal-wrapper"} style={{ paddingTop: 24, paddingBottom: 24 }}>
      <Col span={20} xxl={20} xl={20} lg={20} md={24} sm={24} xs={24}>
        <Title level={3} centered className={"section-title"}>
          <span className={"w300"}>{"You're Signed In to "}</span>
          <span className={"bold"}>{userOrgName}</span>
        </Title>

        <Row justify={"center"}>
          <Col span={18} xxl={18} xl={18} lg={18} md={22} sm={24} xs={24}>
            <Paragraph
              centered
              size={"md"}
              style={{ paddingTop: 24, paddingBottom: 24 }}
              color={"website-secondary"}
              weight={"w300"}
            >
              {`${
                user?.email ? `${user.email} is` : "You are"
              } signed in to the ${userOrgName} portal. ${intentText}\n` +
                "Each portal has its own account, even when the email address is the same. To continue where you are signed in, open your portal below."}
            </Paragraph>
          </Col>
        </Row>

        <Row style={{ marginBottom: 24 }}>
          <Col span={24}>
            {loading ? (
              <Skeleton loading paragraph={{ rows: 2 }} />
            ) : userOrg ? (
              <>
                <OrgItem it={userOrg} onClick={onGoToOwnDashboard} />
                <Paragraph centered size={"sm"} color={"website-secondary"} weight={"w300"} style={{ paddingTop: 8 }}>
                  {`Go to the ${userOrgName} dashboard`}
                </Paragraph>
              </>
            ) : (
              <Row justify={"center"}>
                <Button
                  btnType={"ghost"}
                  textColor={"white"}
                  text={`GO TO ${userOrgName} DASHBOARD`.toUpperCase()}
                  onClick={onGoToOwnDashboard}
                />
              </Row>
            )}
          </Col>
        </Row>
      </Col>

      <Row justify={"center"} className={"divider-with-hex"} style={{ margin: "0 0 24px 0" }}>
        <div className={"hex-small"} />
      </Row>

      <Row justify={"center"}>
        <Col span={18} xxl={18} xl={18} lg={18} md={22} sm={24} xs={24}>
          <Paragraph weight={"w300"} size={"sm"} color={"website-secondary"} centered>
            {`Only one portal session can be active in this browser. Signing in to ${targetOrgName} will sign you out of ${userOrgName}, including in any other open tabs.`}
          </Paragraph>
        </Col>
      </Row>

      <Col span={24} style={{ paddingTop: 32 }}>
        <Row justify={"center"}>
          <Button
            btnType={"primary"}
            text={continueLabel.toUpperCase()}
            loading={isSigningOut}
            onClick={onSignOutAndContinue}
          />
        </Row>
      </Col>
    </Row>
  )
}

export default PortalMismatch
